import { HTTP_METHODS } from 'do-front-end-utils';
import { sendRequestToDhwanoServiceWithCredentials } from '../../utils/request';
import {
  ICloneCampaignResponse,
  IGetActiveInviteCampaignsResponse,
  IGetCampaignResponse,
  IGetCampaignSummaryResponse,
  IGetCampaignTemplateStatusResponse,
  IFeatureConfigTemplateResponse,
} from '../../types/dhwani/campaign.types';

/**
 * Create a new campaign
 * @description This function is used to create a new campaign
 * @param campaign - The campaign payload as FormData
 * @returns The created campaign
 */
export const createCampaign = async (campaign: FormData) => {
  const response = await sendRequestToDhwanoServiceWithCredentials({
    method: HTTP_METHODS.POST,
    url: '/screen/campaign/',
    data: campaign,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response;
};

/**
 * Get a campaign by slug
 * @description This function is used to get a campaign by slug
 * @param campaignSlug - The slug of the campaign
 * @returns The campaign
 */
export const getCampaign = async (campaignSlug: string) => {
  const response =
    await sendRequestToDhwanoServiceWithCredentials<IGetCampaignResponse>({
      method: HTTP_METHODS.GET,
      url: `/screen/campaign/${campaignSlug}/`,
    });
  return response;
};

/**
 * Update a campaign
 * @description This function is used to update a campaign
 * @param campaignSlug - The slug of the campaign
 * @param campaign - The campaign payload as FormData
 * @returns The updated campaign
 */
export const updateCampaign = async (
  campaignSlug: string,
  campaign: FormData
) => {
  const response = await sendRequestToDhwanoServiceWithCredentials({
    method: HTTP_METHODS.PUT,
    url: `/screen/campaign/${campaignSlug}/`,
    data: campaign,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response;
};

/**
 * Archive a campaign
 * @description This function is used to archive a campaign
 * @param campaignSlug - The slug of the campaign
 * @returns The archived campaign
 */
export const archiveCampaignApi = async (campaignSlug: string) => {
  const response = await sendRequestToDhwanoServiceWithCredentials({
    method: HTTP_METHODS.DELETE,
    url: `/screen/campaign/${campaignSlug}/`,
  });
  return response;
};

/**
 * Unarchive a campaign
 * @description This function is used to unarchive a campaign
 * @param campaignSlug - The slug of the campaign
 * @returns The unarchived campaign
 */
export const unarchiveCampaignApi = async (campaignSlug: string) => {
  const response = await sendRequestToDhwanoServiceWithCredentials({
    method: HTTP_METHODS.DELETE,
    url: `/screen/campaign/${campaignSlug}/unarchive/`,
  });
  return response;
};

/**
 * Clone a campaign
 * @description This function is used to clone a campaign
 * @param campaignSlug - The slug of the campaign
 * @param campaignName - The name of the campaign
 * @returns The cloned campaign
 */
export const cloneCampaignApi = async (
  campaignSlug: string,
  campaignName: string
) => {
  const response =
    await sendRequestToDhwanoServiceWithCredentials<ICloneCampaignResponse>({
      method: HTTP_METHODS.POST,
      url: `/screen/campaign/${campaignSlug}/clone/`,
      data: {
        campaign_name: campaignName,
      },
    });
  return response;
};

/**
 * Launch a campaign
 * @description This function is used to launch a campaign
 * @param campaignSlug - The slug of the campaign
 * @returns The launched campaign
 */
export const launchCampaignApi = async (campaignSlug: string) => {
  const response = await sendRequestToDhwanoServiceWithCredentials({
    method: HTTP_METHODS.POST,
    url: `dhwani/communications/schedule/${campaignSlug}/`,
  });
  return response;
};

/**
 * Get a campaign summary
 * @description This function is used to get a campaign summary
 * @param campaignSlug - The slug of the campaign
 * @returns The campaign summary
 */
export const getCampaignSummaryApi = async (campaignSlug: string) => {
  const response =
    await sendRequestToDhwanoServiceWithCredentials<IGetCampaignSummaryResponse>(
      {
        method: HTTP_METHODS.GET,
        url: `/screen/campaign/${campaignSlug}/summary/`,
      }
    );
  return response;
};

/**
 * Get the campaign's Dhwani template status
 * @description Lightweight endpoint designed to be polled - tells whether the
 * campaign's IVR template is ready to be called
 * @param campaignSlug - The slug of the campaign
 * @returns The campaign template status
 */
export const getCampaignTemplateStatusApi = async (campaignSlug: string) => {
  const response =
    await sendRequestToDhwanoServiceWithCredentials<IGetCampaignTemplateStatusResponse>(
      {
        method: HTTP_METHODS.GET,
        url: `/screen/campaign/${campaignSlug}/template-status/`,
      }
    );
  return response;
};

/**
 * Get active invite campaigns for current account
 * @description This function is used to fetch active invite campaigns and their timelines
 * @returns The active invite campaigns list
 */
export const getActiveInviteCampaignsApi = () =>
  sendRequestToDhwanoServiceWithCredentials<IGetActiveInviteCampaignsResponse>({
    method: HTTP_METHODS.GET,
    url: '/screen/campaigns/active-invites/',
  });

/**
 * Export a campaign report
 * @description This function is used to export a campaign report
 * @param campaignSlug - The slug of the campaign
 */
export const exportCampaignReportApi = async (campaignSlug: string) => {
  const response = await sendRequestToDhwanoServiceWithCredentials({
    method: HTTP_METHODS.GET,
    url: `/screen/campaign/${campaignSlug}/report/export/xlsx/`,
    responseType: 'blob',
    headers: {
      'Content-Type':
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    },
  });
  return response;
};

/**
 * Get feature config template
 * @description This function is used to get feature config template
 * @param campaignSlug - The slug of the campaign
 * @param payload - Optional payload object
 * @returns The feature config template response
 */
export const getFeatureConfigTemplate = async (
  campaignSlug: string,
  payload = {}
) => {
  const response =
    await sendRequestToDhwanoServiceWithCredentials<IFeatureConfigTemplateResponse>(
      {
        method: HTTP_METHODS.POST,
        url: `/dhwani/cocaller-feature-config-template/${campaignSlug}/`,
        data: payload,
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
  return response;
};
